import { useEffect, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { useAuth, useUi } from '../lib/store.js';
import { statusPill, statusLabel, statusBusy } from '../lib/format.js';
import { t, useT } from '../lib/i18n.js';

/* ---------------------------------------------------------------- Toasts */

export function ToastHost() {
  const toasts = useAuth((s) => s.toasts);
  const dismiss = useAuth((s) => s.dismissToast);

  return (
    <div className="toast-host" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className="toast" onClick={() => dismiss(t.id)}>
          <span className={t.tone === 'err' ? 'toast-err' : 'toast-ok'}>
            {t.tone === 'err' ? '✕' : '✓'}
          </span>
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- Modal */

export function Modal({
  open, onClose, title, subtitle, children, width = 620, footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  width?: number;
  footer?: ReactNode;
}) {
  const t = useT();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: width }}>
        <div className="row" style={{ padding: '13px 16px', borderBottom: '1px solid var(--line)', alignItems: 'flex-start' }}>
          <div className="grow">
            <div className="h2">{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label={t('Schließen')}>✕</button>
        </div>
        <div style={{ padding: 16 }}>{children}</div>
        {footer && (
          <div className="row" style={{ padding: '11px 16px', borderTop: '1px solid var(--line)', justifyContent: 'flex-end' }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Schalter */

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`switch ${on ? 'switch-on' : ''}`}
      onClick={() => onChange(!on)}
    >
      <div className="switch-knob" />
    </button>
  );
}

/* --------------------------------------------------------------- Zustand */

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span
      className="spin"
      style={{
        display: 'inline-block',
        width: size, height: size,
        border: `2px solid var(--line)`,
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
      }}
    />
  );
}

export function Loading({ text = t('Lädt…') }: { text?: string }) {
  useT();
  return (
    <div className="row" style={{ justifyContent: 'center', padding: 34, gap: 10, color: 'var(--text-3)' }}>
      <Spinner /> <span style={{ fontSize: 13.5 }}>{text}</span>
    </div>
  );
}

export function Empty({
  icon = '◎', title, hint, action,
}: { icon?: string; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card" style={{ padding: '32px 22px', textAlign: 'center' }}>
      <div style={{ fontSize: 26, marginBottom: 9, opacity: 0.45 }}>{icon}</div>
      <div className="h3" style={{ marginBottom: 6 }}>{title}</div>
      {hint && (
        <div className="muted" style={{ fontSize: 12, maxWidth: 440, margin: '0 auto 12px', lineHeight: 1.55 }}>
          {hint}
        </div>
      )}
      {action}
    </div>
  );
}

export function Skeleton({ height = 16, width = '100%', radius = 8 }: { height?: number; width?: number | string; radius?: number }) {
  return <div className="skeleton" style={{ height, width, borderRadius: radius }} />;
}

/* ----------------------------------------------------------- Kennzahlen */

export function Stat({
  label, value, delta, hint, onClick,
}: {
  label: string; value: ReactNode; delta?: string; hint?: string; onClick?: () => void;
}) {
  return (
    <div
      className="card card-pad"
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      <div className="section-label">{label}</div>
      <div className="row" style={{ gap: 7, marginTop: 5, alignItems: 'baseline' }}>
        <div style={{ fontSize: 20, fontWeight: 750, letterSpacing: '-0.025em' }}>{value}</div>
        {delta && (
          <div style={{ fontSize: 12, fontWeight: 600, color: delta.startsWith('-') ? 'var(--err)' : 'var(--ok)' }}>
            {delta}
          </div>
        )}
      </div>
      {hint && <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

/** Kleines Balkendiagramm ohne Bibliothek, reicht für Trendlinien in Kacheln. */
export function Sparkline({ values, color = 'var(--accent)', height = 30 }: { values: number[]; color?: string; height?: number }) {
  const max = Math.max(...values, 1);
  return (
    <div className="row" style={{ gap: 3, alignItems: 'flex-end', height, marginTop: 10 }}>
      {values.map((v, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: `${Math.max(8, (v / max) * 100)}%`,
            background: i === values.length - 1 ? color : 'var(--surface-3)',
            borderRadius: 2,
            minWidth: 3,
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- Diagnose */

export function HealthList({ items }: { items: { key: string; text: string; state: string }[] }) {
  return (
    <div className="col" style={{ gap: 7 }}>
      {items.map((h) => (
        <div key={h.key} className="row" style={{ gap: 8, fontSize: 11.5 }}>
          <div className={`dot dot-${h.state === 'error' ? 'err' : h.state === 'warn' ? 'warn' : h.state === 'ok' ? 'ok' : 'pending'}`} />
          <span style={{ color: h.state === 'ok' ? 'var(--text-2)' : h.state === 'error' ? 'var(--err)' : 'var(--text-3)' }}>
            {h.text}
          </span>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- Bestätigen */

export function Confirm({
  open, title, body, confirmLabel = t('Ja, machen'), danger, onConfirm, onCancel, busy,
  confirmDisabled, width = 440,
}: {
  open: boolean; title: string; body: ReactNode; confirmLabel?: string;
  danger?: boolean; onConfirm: () => void; onCancel: () => void; busy?: boolean;
  /** Sperrt die Bestaetigung, solange im Dialog noch etwas fehlt */
  confirmDisabled?: boolean;
  width?: number;
}) {
  const t = useT();
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      width={width}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>{t('Abbrechen')}</button>
          <button
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
            disabled={busy || confirmDisabled}
          >
            {busy ? <Spinner /> : null} {confirmLabel}
          </button>
        </>
      }
    >
      <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>{body}</div>
    </Modal>
  );
}

/* ------------------------------------------------------- Fokus-Hilfe */

export function useAutoFocus<T extends HTMLElement>(active = true) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (active) ref.current?.focus();
  }, [active]);
  return ref;
}

/** Zeile mit Beschriftung links, Inhalt rechts, für Einstellungsformulare. */
export function Field({
  label, hint, children, style,
}: { label: string; hint?: string; children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={style}>
      <label className="label">{label}</label>
      {children}
      {hint && <div className="dim" style={{ fontSize: 11.5, marginTop: 5, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── Seitenkopf */

/**
 * Kopfzeile einer Seite: Symbol, Titel, rechts die Aktionen.
 *
 * Frueher stand ueber jeder Seite ein Erklaerkasten, dann ein Fragezeichen,
 * hinter dem derselbe Absatz steckte. Beides ist beim ersten Besuch
 * hilfreich und ab dem zweiten nur noch im Weg -- und ein Fragezeichen ueber
 * "Projekte" behauptet, hier gaebe es etwas zu erklaeren, was es nicht gibt.
 *
 * `description` ist deshalb optional und gehoert nur noch dorthin, wo eine
 * Seite wirklich eine Regel hat, die man nicht sehen kann: etwa dass der
 * Autopilot pro Projekt nur vier Posts am Tag absetzt. Wo nichts uebergeben
 * wird, gibt es auch kein Fragezeichen.
 */
export function PageHeader({
  title, description, actions, subtitle, tip,
}: {
  /** Früher ein Symbol vor dem Titel; im neuen Kopf entfällt es. */
  icon?: ReactNode;
  title: ReactNode;
  /** Unsichtbare Regel dieser Seite; erscheint als Tipp „So funktioniert's". */
  description?: ReactNode;
  /** Ein Satz unter dem Titel */
  subtitle?: ReactNode;
  /** Höchstens EINE Primäraktion, dazu optional Sekundäres */
  actions?: ReactNode;
  /** Tipptext; Vorrang vor `description` */
  tip?: ReactNode;
}) {
  return (
    <>
      <PageHead title={title} sub={subtitle} action={actions} />
      {(tip ?? description) ? <Tip>{tip ?? description}</Tip> : null}
    </>
  );
}

/**
 * Der Kopf jeder Seite (README §6): H1, Untertitel, rechts höchstens eine
 * Primäraktion.
 */
export function PageHead({
  title, sub, action,
}: { title: ReactNode; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="grow">
        <h1 className="page-head-title">{title}</h1>
        {sub ? <div className="page-head-sub">{sub}</div> : null}
      </div>
      {action ? <div className="page-head-actions">{action}</div> : null}
    </div>
  );
}

/**
 * Tipp-Banner „So funktioniert's". Global über den Knopf „? Tipps" in der
 * Kopfzeile schaltbar; „Ausblenden" schaltet alle Tipps ab.
 */
export function Tip({ children, title }: { children: ReactNode; title?: string }) {
  const t = useT();
  const on = useUi((s) => s.tipsOn);
  const setTips = useUi((s) => s.setTips);
  if (!on) return null;
  return (
    <div className="tip">
      <span className="tip-mark">i</span>
      <div className="grow">
        <div className="tip-title">{title ?? t('So funktioniert\'s')}</div>
        <div className="tip-text">{children}</div>
      </div>
      <button type="button" className="btn-link" onClick={() => setTips(false)}>{t('Ausblenden')}</button>
    </div>
  );
}

/** Status eines Beitrags als Pille, überall gleich (README §6.12). */
export function StatusPill({
  status, results, style,
}: { status: string; results?: readonly { state: string }[]; style?: CSSProperties }) {
  useT();
  return (
    <span className={statusPill(status, results)} style={style}>
      {statusBusy(status) ? <Spinner size={10} /> : null}
      {statusLabel(status, results)}
    </span>
  );
}

/** Segmentierte Auswahl, z. B. „Woche | Liste" oder „3 · 5 · 7 · 14". */
export function Segmented<T extends string | number>({
  value, options, onChange, ariaLabel,
}: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (v: T) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={o.value === value ? 'seg-on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────── Kennzahlen */

/** Kachel mit eigener Farbe, vier davon nebeneinander lesen sich als Reihe,
 *  nicht als vier gleiche graue Kästen. */
export function Tile({
  label, value, hint, color, delta, onClick,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  color?: string;
  delta?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={`tile ${onClick ? 'tile-clickable' : ''}`}
      onClick={onClick}
      style={color ? ({ ['--tile-color' as string]: color }) : undefined}
    >
      <div className="tile-label">{label}</div>
      <div className="row" style={{ gap: 8, marginTop: 6, alignItems: 'baseline' }}>
        <div className="tile-value">{value}</div>
        {delta && (
          <div style={{ fontSize: 12, fontWeight: 700, color: delta.startsWith('-') ? 'var(--err)' : 'var(--ok)' }}>
            {delta}
          </div>
        )}
      </div>
      {hint && <div className="tile-hint">{hint}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────── Hinweisband */

export function Note({
  tone = 'info', icon, title, children, action,
}: {
  tone?: 'info' | 'warn' | 'err' | 'ok' | 'plain';
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const fallbackIcon = tone === 'warn' ? '!' : tone === 'err' ? '✕' : tone === 'ok' ? '✓' : 'i';
  return (
    <div className={`note ${tone !== 'plain' ? `note-${tone}` : ''}`}>
      <span className="note-icon">{icon ?? fallbackIcon}</span>
      <div className="grow">
        {title && <div style={{ fontWeight: 700, marginBottom: 3 }}>{title}</div>}
        <div style={{ color: 'var(--text2)' }}>{children}</div>
      </div>
      {action && <div style={{ flex: 'none', alignSelf: 'center' }}>{action}</div>}
    </div>
  );
}

/* ──────────────────────────────────────────────────────── Auswahlkarte */

/** Eine Möglichkeit unter mehreren, Ziel, Tonalität, Bildstil, Betriebsart.
 *  Überall dieselbe Form, damit „das kann ich anklicken" nie geraten wird. */
export function Pick({
  on, title, hint, emoji, badge, onClick, style,
}: {
  on: boolean;
  title: ReactNode;
  hint?: ReactNode;
  emoji?: ReactNode;
  badge?: ReactNode;
  onClick: () => void;
  style?: CSSProperties;
}) {
  return (
    <button type="button" className={`pick ${on ? 'pick-on' : ''}`} onClick={onClick} style={style}>
      {on && <span className="pick-check">✓</span>}
      <div className="pick-title">
        {emoji && <span className="pick-emoji">{emoji}</span>}
        <span>{title}</span>
        {badge}
      </div>
      {hint && <div className="pick-hint">{hint}</div>}
    </button>
  );
}

/** Vorschlag zum Anklicken, schreibt sich in ein Textfeld. Nimmt dem Nutzer
 *  die leere Seite weg, die vor jedem Briefing steht. */
export function Suggest({
  label, used, onClick,
}: { label: string; used?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      className={`chip chip-suggest ${used ? 'chip-suggest-used' : ''}`}
      onClick={onClick}
      disabled={used}
    >
      {used ? '✓' : '+'} {label}
    </button>
  );
}

/* ────────────────────────────────────────────────── In die Zwischenablage */

/**
 * Kopiert einen Text und sagt für zwei Sekunden, dass es geklappt hat.
 *
 * Ohne diese Rückmeldung klickt man zweimal, weil nichts passiert zu sein
 * scheint. Die Zwischenablage ist der einzige Vorgang in der Oberfläche, der
 * sonst völlig unsichtbar bliebe.
 */
export function CopyButton({
  text, label = t('Kopieren'), done = t('Kopiert'), title, className = 'btn btn-sm btn-ghost',
}: {
  text: string | (() => string);
  label?: string;
  done?: string;
  title?: string;
  className?: string;
}) {
  useT();
  const [copied, setCopied] = useState(false);

  async function copy() {
    const value = typeof text === 'function' ? text() : text;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Ältere Browser und unsichere Herkunft: der Umweg über ein Feld
      const field = document.createElement('textarea');
      field.value = value;
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      document.execCommand('copy');
      field.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <button
      type="button"
      className={className}
      onClick={() => void copy()}
      title={title}
      style={copied ? { color: 'var(--ok)', borderColor: 'var(--ok-line)' } : undefined}
    >
      {copied ? `✓ ${done}` : label}
    </button>
  );
}
